// Staff/admin auth state (login, logout, user)

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { purgeMobileAdminState, announceAdminSwUser } from '../utils/adminSw';
import { disableAdminPush } from '../utils/adminPush';
import { clearLastActive, isRevocationCode, phoneUnlockArmed, tokenExpMs } from '../utils/mobileLock';
import api from '../utils/api';

const AuthContext = createContext(null);

// How a failed /auth/me maps to state (spec section 8 purge law, four-way):
//   'purge': revoked (version bump, deleted user), or any dead session on a
//            surface without phone unlock armed: cache + token die (M2).
//   'keep': transport failure (the offline cold launch: the SW serves the
//           cached /auth/me and this path never runs), any non-401 answer,
//           or a plain 401 on a phone with an enrolled passkey, where the
//           lock screen owns re-entry and the caches stay, occluded.
function authFailureAction(err) {
  if (err?.status !== 401) return 'keep';
  // ONE revocation list, shared with the lock's 401 claim (utils/mobileLock).
  if (isRevocationCode(err.code)) return 'purge';
  return phoneUnlockArmed() ? 'keep' : 'purge';
}

// A cache-served /auth/me (SW replayed it on transport failure; api.js stamps
// res.staleAt from x-sw-cached-at) proves NOTHING cryptographically: it is a
// snapshot, and offline the server is never consulted. So an offline identity
// is only as good as the token it came with. When that token has died by its
// own clock, refuse to hydrate and wipe the device's copy: otherwise a phone
// that never enrolled a passkey (no lock arms for it, shouldLock returns false
// at !armed) renders a full admin session, and every allowlisted read serves
// cached client PII, indefinitely, long past any expiry. Found by the
// cross-window push review 2026-08-14; introduced by this lane, since before
// it an offline launch simply landed on Login.
//
// Online this never fires: a server-answered response carries no staleAt, and
// a real 401 goes to authFailureAction above.
function refuseStaleHydration(res) {
  if (!res || !res.staleAt) return false; // server answered: authoritative
  let token = null;
  try { token = window.localStorage.getItem('token'); } catch (e) { return true; }
  const exp = tokenExpMs(token);
  return exp !== null && exp <= Date.now();
}

// Every purge of a phone session, in one place (spec 2026-08-13-mobile-admin
// sections 7 and 8): unsubscribe this device from push, purge the SW caches
// and the shell's localStorage (utils/adminSw.js), and drop the token. The
// unsubscribe is LOCAL only (2026-10-14): the token is removed later in this
// same tick, before the unsubscribe's first await resumes, so a server DELETE
// would always go out tokenless, answer 401, and fire a false "session
// expired". Instead the browser drops the subscription, the push service
// answers 410 for that endpoint, and the server prunes the row on its next
// send. Off the admin host the unsubscribe is a no-op (utils/adminPush.js).
// Best effort, never awaited: a purge must not wait on a push service.
function purgePhoneSession() {
  disableAdminPush({ notifyServer: false }).catch(() => {});
  purgeMobileAdminState();
  localStorage.removeItem('token');
}

// Throttle visibilitychange refreshes — admins who tab-flip heavily would
// otherwise trigger /auth/me on every focus event.
const TAB_FOCUS_REFRESH_COOLDOWN_MS = 30_000;

// Shallow-compare auth-relevant fields. /auth/me always returns a fresh
// object, so a literal setUser would re-render every useAuth() consumer
// (sidebar, route guards, layout) on every refresh — even when nothing the
// app cares about changed. Comparing the relevant fields lets us preserve
// the previous reference and skip the cascade.
function isSameUser(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id === b.id
    && a.email === b.email
    && a.role === b.role
    && a.onboarding_status === b.onboarding_status
    && a.has_application === b.has_application
    && a.can_hire === b.can_hire
    && a.can_staff === b.can_staff
    && a.pre_hired === b.pre_hired;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(res => {
          // An offline snapshot never resurrects a session whose token died.
          if (refuseStaleHydration(res)) {
            purgePhoneSession();
            return;
          }
          setUser(res.data.user);
          // Announce the SW cache namespace as soon as the user is KNOWN
          // (security review M1: announcing from AdminLayout misses staff
          // users on the admin origin and races first-paint fetches).
          announceAdminSwUser(res.data.user.id);
        })
        .catch((err) => {
          // The four-way purge law lives in authFailureAction. On 'keep'
          // with a real 401 (expired token, unlock armed) the artifact and
          // caches survive, occluded: ProtectedRoute renders the lock gate
          // and a biometric tap re-mints without losing the offline cache.
          if (authFailureAction(err) === 'purge') {
            purgePhoneSession();
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    announceAdminSwUser(userData.id);
    clearLastActive(); // authenticated in the foreground: no background interval to measure
    // Re-auth in a long-lived PWA document: the expiry handler's once-only
    // guard resets so a LATER expiry can fire again (spec section 8).
    window.dispatchEvent(new Event('session-restored'));
  };

  const logout = () => {
    purgePhoneSession();
    setUser(null);
  };

  // Re-fetch the authenticated user. Call this after mutations that change
  // onboarding_status (e.g. finishing payday protocols) so route guards
  // like RequirePortal see the fresh status without a hard reload.
  // useCallback keeps the identity stable so consumers can safely list it in
  // effect dependency arrays without re-fetching on every render.
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const res = await api.get('/auth/me');
      // Same rule as the bootstrap: an offline snapshot cannot keep a session
      // alive past its own token's expiry.
      if (refuseStaleHydration(res)) {
        purgePhoneSession();
        setUser(null);
        return null;
      }
      const next = res.data.user;
      setUser(prev => (isSameUser(prev, next) ? prev : next));
      return next;
    } catch (err) {
      // Mirror the bootstrap behavior via the same four-way law. On 'keep'
      // with a 401 the phone lock overlay claims the surface through the
      // session-expired event; leaving user state mounted preserves the
      // exact screen the unlock returns to.
      if (authFailureAction(err) === 'purge') {
        purgePhoneSession();
        setUser(null);
      }
      throw err;
    }
  }, []);

  // Pick up role/status changes the moment the user returns to the tab.
  // Auth middleware already reads role from the DB per-request, so server-side
  // permission checks are always fresh — this just keeps the *UI* in sync
  // (sidebar links, route guards) without forcing the user to manually refresh
  // after an admin promotes/deactivates them in another window.
  const lastRefreshAt = useRef(0);
  const refreshInflight = useRef(false);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('token')) return;
      if (refreshInflight.current) return;
      if (Date.now() - lastRefreshAt.current < TAB_FOCUS_REFRESH_COOLDOWN_MS) return;
      refreshInflight.current = true;
      refreshUser()
        .catch(() => { /* swallow — refreshUser already handles 401 */ })
        .finally(() => {
          refreshInflight.current = false;
          lastRefreshAt.current = Date.now();
        });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
